import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

export const DEFAULT_CURRENCY = { currency: 'DZD', currencySymbol: 'DA', currencyPosition: 'AFTER' };

const numberFormat = new Intl.NumberFormat('fr-DZ', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Formats an amount with the system currency, e.g. "1 000 DA" (AFTER) or "DA 1 000" (BEFORE).
 * Pure helper for code that can't call the hook (utils, chart formatters, tests).
 */
export const formatMoney = (amount, settings = DEFAULT_CURRENCY) => {
  if (amount === null || amount === undefined || amount === '' || Number.isNaN(Number(amount))) return '—';
  const { currency, currencySymbol, currencyPosition } = { ...DEFAULT_CURRENCY, ...settings };
  const formatted = numberFormat.format(Number(amount));
  const symbol = currencySymbol || currency;
  return currencyPosition === 'BEFORE' ? `${symbol} ${formatted}` : `${formatted} ${symbol}`;
};

/**
 * The system-wide currency (set by the admin in Paramètres › Devise & Système). It arrives with
 * login / `me` inside `auth.user`; DZD/DA/AFTER until then. Never hardcode "DZD" in the UI — use:
 *   const { format } = useCurrency();  ...  <span>{format(price)}</span>
 */
export const useCurrency = () => {
  const user = useSelector((state) => state.auth.user);
  const currency = user?.currency || DEFAULT_CURRENCY.currency;
  const currencySymbol = user?.currencySymbol || DEFAULT_CURRENCY.currencySymbol;
  const currencyPosition = user?.currencyPosition || DEFAULT_CURRENCY.currencyPosition;

  const format = useCallback(
    (amount) => formatMoney(amount, { currency, currencySymbol, currencyPosition }),
    [currency, currencySymbol, currencyPosition]
  );

  return useMemo(
    () => ({ currency, currencySymbol, currencyPosition, format }),
    [currency, currencySymbol, currencyPosition, format]
  );
};

export default useCurrency;
